/**
 * Domain models, ported from the iOS app's `Models/` directory.
 *
 * The Swift originals carry `CodingKeys` that map every property to snake_case
 * for the wire. Nothing here is serialised — the mock layer is in-bundle and
 * hands these objects straight to React — so the port keeps camelCase and drops
 * the mapping. If a live backend is ever wired in, that mapping is the seam
 * that has to come back.
 */

/* ------------------------------------------------------------------ */
/* Post — absorbs events, activities, rivals, scholarships and jobs    */
/* ------------------------------------------------------------------ */

export type PostType = "regular" | "event" | "activity" | "rival" | "scholarship" | "job";

export interface PostReward {
  /** "1st Place", "Award", "Pay" */
  label: string;
  /** "$500", "50 Frosh Points", "$20/hr" */
  value: string;
}

export interface PostAuthor {
  id: string;
  displayName: string;
  profilePhotoURL?: string;
  school?: string;
  isVerified: boolean;
}

export interface PostClub {
  id: string;
  name: string;
  profilePhotoURL?: string;
  school?: string;
}

export interface Post {
  id: string;
  content: string;
  mediaURL?: string;
  /** "video" | "photo" | "text" */
  mediaType?: string;
  /**
   * What the photo actually shows, written once when the media is uploaded
   * rather than re-derived per viewer. A picture is the same picture for
   * everyone who opens it. Video is deliberately excluded: those questions are
   * time-indexed, so one description of a whole clip answers none of them.
   */
  mediaDescription?: string;
  thumbnailURL?: string;
  title?: string;
  likeCount: number;
  commentCount: number;
  shareCount: number;
  viewCount: number;
  isLikedByMe: boolean;
  isSavedByMe: boolean;
  author: PostAuthor;
  hashtags: string[];
  location?: string;
  competitionId?: string;
  createdAt: string;

  postType: PostType;

  // Event
  eventDate?: string;
  eventEndDate?: string;
  eventLocation?: string;
  isVirtual?: boolean;
  attendeeCount?: number;
  isRSVPd?: boolean;
  matchesAttending?: MatchUser[];
  groupChatId?: string;

  // Activity / rival
  category?: string;
  isOpen?: boolean;
  respondentCount?: number;
  isJoinedByMe?: boolean;
  acceptedBy?: PostAuthor;

  // Invitation
  isInvited?: boolean;
  invitedBy?: PostAuthor;

  // Match feed
  isFromMatchedUser?: boolean;

  // Classmate
  sharedCourse?: string;

  /**
   * Course linkage for the Learn feed. `courseId` drives logic; `courseCode` is
   * denormalised so a cell renders "CS 101" without an async lookup — the badge
   * is the whole point of the linkage.
   */
  courseId?: string;
  courseCode?: string;

  club?: PostClub;

  hasReward?: boolean;
  rewards?: PostReward[];

  // Shared application fields (scholarship + job)
  isAppliedByMe?: boolean;
  applicationURL?: string;
  applicationDeadline?: string;

  // Scholarship
  scholarshipAmount?: number;
  scholarshipProvider?: string;
  eligibilityCriteria?: string;

  // Job
  employerName?: string;
  compensationAmount?: string;
  isRemote?: boolean;

  // Featured promotion window
  featuredFrom?: string;
  featuredUntil?: string;
}

/** Computed in Swift as `Post.isFeatured`; a function here since TS has no stored computeds. */
export function isFeatured(post: Post, now: Date = new Date()): boolean {
  if (!post.featuredFrom || !post.featuredUntil) return false;
  const start = Date.parse(post.featuredFrom);
  const end = Date.parse(post.featuredUntil);
  if (Number.isNaN(start) || Number.isNaN(end)) return false;
  const t = now.getTime();
  return t >= start && t <= end;
}

/* ------------------------------------------------------------------ */
/* User                                                                */
/* ------------------------------------------------------------------ */

export interface User {
  id: string;
  primaryEmail: string;
  displayName: string;
  profilePhotoURL?: string;
  school?: string;
  major?: string;
  year?: string;
  isStudentVerified: boolean;
  froshPoints: number;
  vibeAnalysisText?: string;
  zodiacSign?: string;
  createdAt: string;
}

/* ------------------------------------------------------------------ */
/* Academic                                                            */
/* ------------------------------------------------------------------ */

export interface Professor {
  id: string;
  displayName: string;
  department?: string;
  email?: string;
  officeLocation?: string;
  officeHours?: string;
  photoURL?: string;
  averageRating?: number;
  ratingCount: number;
}

export interface ProfessorReview {
  id: string;
  professorId: string;
  courseId?: string;
  rating: number;
  text?: string;
  author: PostAuthor;
  createdAt: string;
}

export interface Course {
  id: string;
  name: string;
  code?: string;
  /** A Professor, never a bare string. */
  professor?: Professor;
  schedule?: string;
  location?: string;
  credits?: number;
  currentGrade?: number;
  officeHours?: string;
  syllabusURL?: string;
}

export type AssignmentStatus = "upcoming" | "inProgress" | "submitted" | "graded" | "overdue";

export const ASSIGNMENT_STATUS_LABEL: Record<AssignmentStatus, string> = {
  upcoming: "Not Started",
  inProgress: "In Progress",
  submitted: "Submitted",
  graded: "Finished",
  overdue: "Overdue",
};

export interface Assignment {
  id: string;
  courseId: string;
  title: string;
  dueDate: string;
  status: AssignmentStatus;
  grade?: number;
  weight?: number;
  description?: string;
}

export interface StudyGroup {
  id: string;
  courseId: string;
  name: string;
  memberCount: number;
  nextSession?: string;
  sharedConfusionAreas: string[];
  groupChatId?: string;
  photoURL?: string;
}

export interface LectureNote {
  id: string;
  courseId: string;
  title: string;
  content: string;
  conceptTags: string[];
  author: PostAuthor;
  isBestNote: boolean;
  recordedAt: string;
}

export interface CourseAnnouncement {
  id: string;
  courseId: string;
  title: string;
  body: string;
  emoji?: string;
  author: PostAuthor;
  isPinned: boolean;
  postedAt: string;
}

export interface GradeScenario {
  description: string;
  projectedGrade: number;
}

export interface GradeProjection {
  courseId?: string;
  currentGrade: number;
  projectedGrade: number;
  scenarios: GradeScenario[];
}

export interface GPASnapshot {
  id: string;
  date: string;
  gpa: number;
}

export interface YouTubeVideo {
  /** YouTube video ID */
  id: string;
  title: string;
  channelName: string;
  likeCount: number;
  commentCount: number;
  shareCount: number;
  isLiked: boolean;
  isSaved: boolean;
  isNoted: boolean;
}

/* ------------------------------------------------------------------ */
/* Calendar                                                            */
/* ------------------------------------------------------------------ */

export type AcademicDateCategory =
  | "officeHours"
  | "dropDeadline"
  | "examPeriod"
  | "registrationOpen"
  | "holiday";

export interface AcademicDate {
  id: string;
  title: string;
  date: string;
  category: AcademicDateCategory;
  courseId?: string;
  notes?: string;
}

export type CalendarEntryKind =
  | { type: "assignment"; assignment: Assignment }
  | { type: "event"; post: Post }
  | { type: "studyGroup"; studyGroup: StudyGroup }
  | { type: "academicDate"; academicDate: AcademicDate }
  | { type: "activity"; post: Post }
  | { type: "rival"; post: Post };

export interface CalendarEntry {
  id: string;
  kind: CalendarEntryKind;
  date: Date;
  endDate?: Date;
  title: string;
  subtitle?: string;
  isAllDay: boolean;
  /** Icon token resolved by the component layer, not an SF Symbol name. */
  icon: string;
  /** A token name from the design system, not a raw colour. */
  tone: CalendarTone;
}

export type CalendarTone =
  | "assignment"
  | "event"
  | "invited"
  | "studyGroup"
  | "officeHours"
  | "deadline"
  | "exam"
  | "registration"
  | "holiday"
  | "activity"
  | "rival";

/* ------------------------------------------------------------------ */
/* Matching                                                            */
/* ------------------------------------------------------------------ */

export interface MatchUser {
  id: string;
  displayName: string;
  profilePhotoURL?: string;
  school?: string;
  major?: string;
  year?: string;
  voiceNoteURL?: string;
}

export interface Match {
  id: string;
  user: MatchUser;
  overallScore: number;
  zodiacMultiplier?: number;
  matchedAt: string;
}

export interface OCEANScores {
  openness: number;
  conscientiousness: number;
  extraversion: number;
  agreeableness: number;
  neuroticism: number;
}

export interface VibeProfile {
  id: string;
  username: string;
  vibeAnalysisText?: string;
  profileDepth: number;
  oceanScores?: OCEANScores;
  mbtiType?: string;
  enneagramType?: string;
  zodiacSign?: string;
  natalChartURL?: string;
  /**
   * Free-text tags — what this person is actually into. Personality types
   * describe how someone operates; these are what you'd open a conversation with.
   */
  interests?: string[];
}

export interface SubScore {
  dimension: string;
  score: number;
  label: string;
}

export interface CompatibilityResult {
  overallScore: number;
  subScores: SubScore[];
  narrative?: string;
  zodiacMultiplier?: number;
  /**
   * Interests both people listed, in the viewer's own order. Deliberately not
   * part of the score — OCEAN decides compatibility; this is the opening line.
   */
  sharedInterests?: string[];
}

export interface QuestionOption {
  id: string;
  text: string;
  value: number;
}

export interface AssessmentQuestion {
  id: string;
  text: string;
  dimension: string;
  options: QuestionOption[];
}

/* ------------------------------------------------------------------ */
/* Messaging — retained for shape; Telegram is the actual inbox        */
/* ------------------------------------------------------------------ */

export type ConversationCategory = "classmates" | "matches" | "groups" | "other" | "requests";

export const CONVERSATION_CATEGORY_LABEL: Record<ConversationCategory, string> = {
  classmates: "Classmates",
  matches: "Matches",
  groups: "Groups",
  other: "Other",
  requests: "Requests",
};

export interface ConversationParticipant {
  id: string;
  displayName: string;
  profilePhotoURL?: string;
}

export interface Message {
  id: string;
  conversationId: string;
  senderId: string;
  text: string;
  imageURL?: string;
  videoURL?: string;
  isRead: boolean;
  createdAt: string;
}

export interface Conversation {
  id: string;
  participants: ConversationParticipant[];
  lastMessage?: Message;
  unreadCount: number;
  category: ConversationCategory;
  isGroup: boolean;
  groupName?: string;
  groupAvatarURL?: string;
  createdAt: string;
}

/* ------------------------------------------------------------------ */
/* Social                                                              */
/* ------------------------------------------------------------------ */

export interface Club {
  id: string;
  name: string;
  description: string;
  memberCount: number;
  isMember: boolean;
  nextMeeting?: string;
  imageURL?: string;
  groupChatId?: string;
}

export interface Story {
  id: string;
  author: PostAuthor;
  mediaURL: string;
  /** "image" | "video" | "text" */
  mediaType: string;
  textContent?: string;
  styleTemplate?: string;
  isViewed: boolean;
  expiresAt: string;
  createdAt: string;
}

export interface Comment {
  id: string;
  postId: string;
  text: string;
  likeCount: number;
  isLikedByMe: boolean;
  author: PostAuthor;
  createdAt: string;
}

export type ReportReason =
  | "spam"
  | "harassment"
  | "inappropriateContent"
  | "misinformation"
  | "other";

export const REPORT_REASON_LABEL: Record<ReportReason, string> = {
  spam: "Spam",
  harassment: "Harassment",
  inappropriateContent: "Inappropriate Content",
  misinformation: "Misinformation",
  other: "Other",
};

export type ReportableContentType = "post" | "comment" | "message" | "user";

/* ------------------------------------------------------------------ */
/* Challenges, competitions, rewards, marketplace                      */
/* ------------------------------------------------------------------ */

export interface Challenge {
  id: string;
  prompt: string;
  date: string;
  isActive: boolean;
  submissionCount: number;
  expiresAt?: string;
}

export interface Competition {
  id: string;
  title: string;
  entryCount: number;
  createdAt: string;
}

/**
 * Reward currency. Presented exclusively as campus perks, rewards and
 * achievements — never as tokens, coins or a wallet. Hard product constraint.
 */
export interface RewardBalance {
  balance: number;
  rank?: number;
  campusRank?: number;
}

export interface RewardHistory {
  id: string;
  amount: number;
  reason: string;
  earnedAt: string;
}

export interface MarketplaceItem {
  id: string;
  title: string;
  description: string;
  price: number;
  category: string;
  imageURLs: string[];
  seller: PostAuthor;
  campus?: string;
  isSold: boolean;
  createdAt: string;
}

/* ------------------------------------------------------------------ */
/* Horoscope                                                           */
/* ------------------------------------------------------------------ */

export interface Horoscope {
  sign: string;
  date: string;
  headline: string;
  body: string;
  mood: string;
  luckyNumber: number;
  compatibleSign: string;
}

/** Canonical sign order — also the index used for per-sign reading offsets. */
export const ZODIAC_SIGNS = [
  "Aries",
  "Taurus",
  "Gemini",
  "Cancer",
  "Leo",
  "Virgo",
  "Libra",
  "Scorpio",
  "Sagittarius",
  "Capricorn",
  "Aquarius",
  "Pisces",
] as const;

export const ZODIAC_SYMBOLS: Record<string, string> = {
  Aries: "♈️",
  Taurus: "♉️",
  Gemini: "♊️",
  Cancer: "♋️",
  Leo: "♌️",
  Virgo: "♍️",
  Libra: "♎️",
  Scorpio: "♏️",
  Sagittarius: "♐️",
  Capricorn: "♑️",
  Aquarius: "♒️",
  Pisces: "♓️",
};

export const ZODIAC_ELEMENTS: Record<string, string> = {
  Aries: "Fire",
  Leo: "Fire",
  Sagittarius: "Fire",
  Taurus: "Earth",
  Virgo: "Earth",
  Capricorn: "Earth",
  Gemini: "Air",
  Libra: "Air",
  Aquarius: "Air",
  Cancer: "Water",
  Scorpio: "Water",
  Pisces: "Water",
};

export function zodiacSymbol(sign: string): string {
  return ZODIAC_SYMBOLS[sign] ?? "✨";
}

export function zodiacElement(sign: string): string {
  return ZODIAC_ELEMENTS[sign] ?? "";
}

export function zodiacIndex(sign: string): number {
  const i = (ZODIAC_SIGNS as readonly string[]).indexOf(sign);
  return i < 0 ? 0 : i;
}

/* ------------------------------------------------------------------ */
/* Dash & search                                                       */
/* ------------------------------------------------------------------ */

export type DashWidgetType =
  | "upcomingAssignments"
  | "todayChallenge"
  | "newMatches"
  | "unreadMessages"
  | "upcomingEvents"
  | "froshPoints"
  | "froshBotNudge"
  | "campusNews"
  | "leaderboardPosition"
  | "studyGroupActivity";

export interface DashWidget {
  id: string;
  type: DashWidgetType;
  title: string;
  subtitle?: string;
  value?: string;
  deepLinkTab?: string;
  deepLinkSection?: string;
}

export type SearchResultType = "user" | "post" | "hashtag" | "topic" | "event" | "club";

export interface SearchResult {
  id: string;
  type: SearchResultType;
  title: string;
  subtitle?: string;
  imageURL?: string;
}
